import createClient from 'openapi-fetch';
import type { paths, components, operations } from '../generated/api';
import { collectAttribution } from './attribution';
import { parseMockFlags } from './mockFlags';
import { parseMockFaults } from './mockFaults';
import { parseMockScale, scaleShops } from './mockScale';

// Re-export schema types under the names components already import
export type FeedUploadResult = components['schemas']['FeedUploadResponse'];

// Catalog coverage (feed-coverage-visibility). coveragePercent is null — never
// 0 — when volume is too low to state a share honestly, so a new publisher is
// never told their coverage is zero.
export type SupplyState = 'none' | 'preparing' | 'active' | 'failing' | 'stopped' | 'paused' | 'repairing';

/**
 * One shop, and everything the page needs to decide about it.
 *
 * Replaces `AvailableAdvertiser` + `ShopPerformance` + `Feed`, which described
 * the same shop three times from three endpoints that could not see each other.
 * `supplied` is the field that matters most, and it is WIDER than
 * `supply !== 'none'`: it is also true for a shop whose products already arrive
 * through another row's feed. Gate the add action on it, never on `supply`, or
 * the page offers to add a shop the publisher already sells from — which
 * duplicates that shop's products in every match.
 */
export interface Shop {
  /** `<network>:<programmeKey>`, or `feed:<id>` for a feed matching no programme. */
  key: string;
  network: string;
  programmeKey: string | null;
  advertiserHost: string | null;
  name: string | null;
  category: string | null;
  label: string;

  supply: SupplyState;
  supplied: boolean;
  /** An active subscription row. NARROWER than `supplied` — not a substitute. */
  subscribed: boolean;

  feedId: number | null;
  catalogueId: number | null;
  productCount: number | null;
  /** The PROGRAMME's size, not what the publisher will receive. */
  programmeItemCount: number | null;

  sourceUrlDisplay: string | null;
  /** We authored this URL (a network export we build), so it is not editable. */
  sourceManaged: boolean;
  syncEnabled: boolean | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  lastUploadAt: string | null;
  declaredAdvertiserHost: string | null;

  /** Hosts a multi-advertiser feed covers. Empty unless this row IS such a feed. */
  coversHosts: string[];
  /** A feed AND a subscription for one shop: its products are served twice. */
  duplicateSupply: boolean;
  suppliedByFeedLabel: string | null;

  commissionRate: number | null;
  cookieDays: number | null;
  /** true funded, false out of budget, null not stated by the network. */
  funded: boolean | null;
  /** The network's own status word, verbatim. */
  programmeStatus: string | null;
  /** [0,1]. Zero means nothing has been measured, NOT that it measured badly. */
  priorConfidence: number;
  /** Why it sits where it does. Rendered verbatim; never matched on. */
  priorReasons: string[];

  approvalAmountPct: number | null;
  approvalCountPct: number | null;
  epcRank: number | null;
  epcRankOf: number | null;
  epcValue: number | null;
  epcCurrency: string | null;
  paymentDays: number | null;
  locksInWindow: number | null;
  lockWindowDays: number;
}

export interface ShopOutcomeRow {
  feedId: number;
  shows: number;
  clicks: number;
  /** Shown enough to judge, and never clicked — the removal signal. */
  shownNeverClicked: boolean;
}

export interface ShopOutcome {
  windowDays: number;
  minShowsToJudge: number;
  rows: ShopOutcomeRow[];
}

export interface ShopOffer {
  /** Served, not written in the component — it describes a server behaviour. */
  consent: string;
  shops: Shop[];
}

export interface SubscribeResult {
  success: boolean;
  catalogueId: number;
  /** 'preparing' for the first subscriber, who waits; 'ready' for anyone after. */
  state: string;
  consent?: string;
}

export interface CatalogCoverage {
  success: boolean;
  status: 'not-computed' | 'no-data' | 'low-volume' | 'ok';
  windowDays?: number;
  requests?: number;
  matched?: number;
  missedNoDescription?: number;
  missedNoProducts?: number;
  coveragePercent?: number | null;
  uncoveredGroups?: Array<{ subject: string; images: number }>;
  uncoveredRemainder?: number;
  computedAt?: string;
}

// Result of inspecting a feed file without importing it (feed-column-mapping).
export interface FeedInspectResult {
  success: boolean;
  autoResolved: boolean;          // the network vocabulary handled it; nothing to choose
  headers: string[];              // the file's actual columns
  sampleRows: string[][];
  mapping: Record<string, string>; // our field -> their header
  samples: Record<string, string>; // our field -> a real value from that column
  unresolved: string[];           // required fields still without a column
  mustConfirm: string[];          // fields needing explicit confirmation (always includes 'link')
}
export type FeedHistoryItem = components['schemas']['FeedHistoryItem'];
export type FeedStats = components['schemas']['FeedStatsItem'];
export type LoginResponse = { token: string; user: components['schemas']['User'] };
export type SignupResponse = components['schemas']['SignupResponse'];
export type ProfileResponse = components['schemas']['ProfileResponse'];
export type VerificationInfoResponse = components['schemas']['VerificationInfoResponse'];
export type VerifyWebsiteResponse = components['schemas']['VerifyWebsiteResponse'];
export type UserAnalyticsResponse = components['schemas']['UserAnalyticsResponse'];
export type AnalyticsDailyPoint = components['schemas']['AnalyticsDailyPoint'];
export type AnalyticsPeriod = 'today' | 'yesterday' | 'week' | '30d' | '90d';
export type AnalyticsFeedRow = components['schemas']['AnalyticsFeedRow'];
export type AnalyticsEstimate = components['schemas']['AnalyticsEstimate'];
export type AnalyticsClickLedgerEntry = components['schemas']['AnalyticsClickLedgerEntry'];
export type Feed = components['schemas']['Feed'];
export type UserUsageResponse = components['schemas']['UserUsageResponse'];
export type SetupStatusResponse = components['schemas']['SetupStatusResponse'];

/**
 * A publisher's account AT a network. Several at once is the normal case.
 * `syncState: 'never_connected'` is a HEALTHY resting state — a connection
 * carrying only a feed is complete, and rendering it as a fault would put a
 * warning against a publisher who has done nothing wrong.
 */
export type NetworkConnection = components['schemas']['NetworkConnection'];

/** Whether we should be asking this publisher to measure, computed from their own traffic. */
export type NetworkReadiness = components['schemas']['NetworkReadiness'];

export type MeasuredEarningsRow = components['schemas']['MeasuredEarningsRow'];
export type UnattributedSummaryRow = components['schemas']['UnattributedSummary'];
export type UnattributedByNetworkRow = components['schemas']['UnattributedByNetwork'];
export type ConversionRate = components['schemas']['ConversionRate'];
export type IngestReport = components['schemas']['IngestReport'];

export type MeasuredEarningsResponse =
  NonNullable<operations['getMeasuredEarnings']['responses'][200]['content']['application/json']>;

/**
 * Every shape above is taken from the generated contract rather than
 * hand-written, which is this file's dominant pattern. Three of them WERE
 * hand-written until the simplifier pass on PR #122, and they had already
 * drifted: the local `NetworkReadiness` declared `connectionId: number | null`
 * where the contract says optional, which is exactly why the two reads below
 * needed `as` casts to compile. A cast over a duplicated type is the contract
 * quietly ceasing to be enforced.
 */

/** Success and failure both render in place — the message is the API's own advice. */
export interface ConnectCredentialResult {
  success: boolean;
  synced?: boolean;
  report?: IngestReport;
  caveat?: string | null;
  message?: string;
}

export interface ConversionUploadResult {
  success: boolean;
  uploadId?: number;
  report?: IngestReport;
  message?: string;
}
export type DebugTokenResponse = components['schemas']['DebugTokenResponse'];

// ---------------------------------------------------------------------------
// Mock data
// ---------------------------------------------------------------------------

const mockFeedHistory: FeedHistoryItem[] = [
  { feed_ref: 12, network: '2performant', filename: 'sync-feed-12.csv', total_products: 56738, processed_products: 56701, skipped_products: 37, processing_time: 12400, success_rate: 99.93, created_at: '2026-09-04T04:02:00Z' },
  { feed_ref: 8, network: '2performant', filename: 'sync-feed-8.csv', total_products: 1216, processed_products: 1216, skipped_products: 0, processing_time: 900, success_rate: 100.0, created_at: '2026-09-04T04:01:00Z' },
  { feed_ref: 11, network: '2performant', filename: 'sync-feed-11.csv', total_products: 8342, processed_products: 8300, skipped_products: 42, processing_time: 3100, success_rate: 99.5, created_at: '2026-09-02T04:01:00Z' },
  { feed_ref: 18, network: '2performant', filename: 'various-brands.csv', total_products: 4102, processed_products: 4090, skipped_products: 12, processing_time: 2600, success_rate: 99.71, created_at: '2026-08-30T11:20:00Z' },
];

const mockFeedStats: FeedStats[] = [
  { network: '2performant', feed_count: 6, total_products: 71660, processed_products: 71547, avg_processing_time: 4800, last_upload: '2026-09-04T04:02:00Z' },
];

// Mirrors the API's redactSourceUrl (host + last path segment, no query) so
// mock-mode PATCH/POST responses behave like the real write-only contract.
function redactMockUrl(rawUrl: string | null | undefined): string | null {
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl);
    const segments = url.pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    return last ? `${url.host}/${last}` : url.host;
  } catch {
    return '(unparseable URL)';
  }
}

// Mutable so PATCH /feeds/{id} mocks can persist an EPC/sync edit for the
// duration of a mock session, and GET /feeds reflects it back.
const mockFeeds: Feed[] = [
  { id: 12, network: '2performant', label: 'answear', productCount: 56738, createdAt: '2026-06-01T10:00:00Z', lastUploadAt: '2026-09-04T04:02:00Z', epcEstimate: 0.35, epcCurrency: 'RON', sourceUrlDisplay: 'network.2performant.com/export.csv', syncEnabled: true, lastSyncedAt: '2026-09-04T04:02:00Z', lastSyncStatus: 'ok', lastSyncError: null, declaredAdvertiserHost: null },
  { id: 8, network: '2performant', label: 'dyfashion', productCount: 1216, createdAt: '2026-06-01T10:00:00Z', lastUploadAt: '2026-09-04T04:01:00Z', epcEstimate: null, epcCurrency: null, sourceUrlDisplay: 'network.2performant.com/export.csv', syncEnabled: true, lastSyncedAt: '2026-09-04T04:01:00Z', lastSyncStatus: 'ok', lastSyncError: null, declaredAdvertiserHost: null },
  { id: 11, network: '2performant', label: 'kitunghii', productCount: 8342, createdAt: '2026-06-05T10:00:00Z', lastUploadAt: '2026-09-02T04:01:00Z', epcEstimate: null, epcCurrency: null, sourceUrlDisplay: 'network.2performant.com/export.csv', syncEnabled: false, lastSyncedAt: '2026-09-02T04:01:00Z', lastSyncStatus: 'ok', lastSyncError: null, declaredAdvertiserHost: null },
  { id: 9, network: '2performant', label: 'nailsup', productCount: 11191, createdAt: '2026-06-05T10:00:00Z', lastUploadAt: '2026-09-01T04:01:00Z', epcEstimate: null, epcCurrency: null, sourceUrlDisplay: 'network.2performant.com/export.csv', syncEnabled: true, lastSyncedAt: '2026-09-01T04:01:00Z', lastSyncStatus: 'error', lastSyncError: 'Feed source responded with HTTP 503', declaredAdvertiserHost: null },
  { id: 21, network: '2performant', label: 'sofiline', productCount: 3210, createdAt: '2026-06-10T10:00:00Z', lastUploadAt: '2026-08-28T04:01:00Z', epcEstimate: null, epcCurrency: null, sourceUrlDisplay: 'network.2performant.com/export.csv', syncEnabled: false, lastSyncedAt: '2026-08-28T04:01:00Z', lastSyncStatus: 'error', lastSyncError: 'Feed source responded with HTTP 404 (paused after 3 consecutive failures)', declaredAdvertiserHost: null },
  { id: 18, network: '2performant', label: 'various-brands', productCount: 4102, createdAt: '2026-06-12T10:00:00Z', lastUploadAt: '2026-08-30T11:20:00Z', epcEstimate: null, epcCurrency: null, sourceUrlDisplay: null, syncEnabled: false, lastSyncedAt: null, lastSyncStatus: null, lastSyncError: null, declaredAdvertiserHost: null },
];

/**
 * The same account as `mockFeeds`, seen as shops.
 *
 * Ordered as the SERVER would order it — by the prior, then catalogue size — so
 * mock mode shows the real shape and the portal has nothing to re-sort by. Every
 * supply state the portal renders appears exactly once, because a state with no
 * fixture is a branch nobody looks at until a publisher hits it.
 */
const mockShops: Shop[] = [
  // Supplied by her own feed, syncing normally. Before the join existed this
  // row carried an `Add` button.
  { key: '2performant:907', network: '2performant', programmeKey: '907', advertiserHost: 'answear.ro', name: 'answear.ro', category: 'Fashion', label: 'answear.ro',
    supply: 'active', supplied: true, subscribed: false, feedId: 12, catalogueId: null, productCount: 56738, programmeItemCount: 56738,
    sourceUrlDisplay: 'network.2performant.com/export.csv', sourceManaged: true, syncEnabled: true, lastSyncedAt: '2026-09-04T04:02:00Z', lastSyncError: null, lastUploadAt: '2026-09-04T04:02:00Z', declaredAdvertiserHost: null,
    coversHosts: [], duplicateSupply: false, suppliedByFeedLabel: null,
    commissionRate: 7, cookieDays: 15, funded: true, programmeStatus: 'active', priorConfidence: 0.8,
    priorReasons: ['EPC at 90% of its network, shrunk for click volume at 99%.', 'Approves 64% of commissions by value.'],
    approvalAmountPct: 64, approvalCountPct: 76, epcRank: 52, epcRankOf: 507, epcValue: null, epcCurrency: null, paymentDays: 59, locksInWindow: 0, lockWindowDays: 90 },
  { key: '2performant:5192', network: '2performant', programmeKey: '5192', advertiserHost: 'kitunghii.ro', name: 'kitunghii.ro', category: 'Beauty', label: 'kitunghii.ro',
    supply: 'paused', supplied: true, subscribed: false, feedId: 11, catalogueId: null, productCount: 8342, programmeItemCount: 8342,
    sourceUrlDisplay: 'network.2performant.com/export.csv', sourceManaged: true, syncEnabled: false, lastSyncedAt: '2026-09-02T04:01:00Z', lastSyncError: null, lastUploadAt: '2026-09-02T04:01:00Z', declaredAdvertiserHost: null,
    coversHosts: [], duplicateSupply: false, suppliedByFeedLabel: null,
    commissionRate: 15, cookieDays: 30, funded: true, programmeStatus: 'active', priorConfidence: 0.8,
    priorReasons: ['EPC at 88% of its network, shrunk for click volume at 96%.', 'Approves 93% of commissions by value.'],
    approvalAmountPct: 93, approvalCountPct: 91, epcRank: 61, epcRankOf: 507, epcValue: null, epcCurrency: null, paymentDays: 30, locksInWindow: 0, lockWindowDays: 90 },
  // Failing but still retried — distinct from the struck-out row below, and the
  // distinction is the whole reason `stopped` exists.
  { key: '2performant:8036', network: '2performant', programmeKey: '8036', advertiserHost: 'nailsup.ro', name: 'nailsup.ro', category: 'Beauty', label: 'nailsup.ro',
    supply: 'failing', supplied: true, subscribed: false, feedId: 9, catalogueId: null, productCount: 11191, programmeItemCount: 11191,
    sourceUrlDisplay: 'network.2performant.com/export.csv', sourceManaged: true, syncEnabled: true, lastSyncedAt: '2026-09-01T04:01:00Z', lastSyncError: 'Feed source responded with HTTP 503', lastUploadAt: '2026-09-01T04:01:00Z', declaredAdvertiserHost: null,
    coversHosts: [], duplicateSupply: false, suppliedByFeedLabel: null,
    commissionRate: 15, cookieDays: 30, funded: true, programmeStatus: 'active', priorConfidence: 0.8,
    priorReasons: ['EPC at 94% of its network, shrunk for click volume at 62%.', 'Approves 99% of commissions by value.'],
    approvalAmountPct: 99, approvalCountPct: 98, epcRank: 29, epcRankOf: 507, epcValue: null, epcCurrency: null, paymentDays: 28, locksInWindow: 0, lockWindowDays: 90 },
  // Supplied twice: a feed AND a subscription, so its products are served twice.
  { key: '2performant:2405', network: '2performant', programmeKey: '2405', advertiserHost: 'dyfashion.ro', name: 'dyfashion.ro', category: 'Fashion', label: 'dyfashion.ro',
    supply: 'active', supplied: true, subscribed: true, feedId: 8, catalogueId: 4, productCount: 1216, programmeItemCount: 1216,
    sourceUrlDisplay: 'network.2performant.com/export.csv', sourceManaged: true, syncEnabled: true, lastSyncedAt: '2026-09-04T04:01:00Z', lastSyncError: null, lastUploadAt: '2026-09-04T04:01:00Z', declaredAdvertiserHost: null,
    coversHosts: [], duplicateSupply: true, suppliedByFeedLabel: null,
    commissionRate: 10, cookieDays: 30, funded: true, programmeStatus: 'active', priorConfidence: 0.8,
    priorReasons: ['EPC at 46% of its network, shrunk for click volume at 95%.'],
    approvalAmountPct: 71, approvalCountPct: 74, epcRank: 210, epcRankOf: 507, epcValue: null, epcCurrency: null, paymentDays: 30, locksInWindow: 0, lockWindowDays: 90 },
  // Struck out by the scheduler after three failures. NOT "paused by you".
  { key: '2performant:5151', network: '2performant', programmeKey: '5151', advertiserHost: 'sofiline.ro', name: 'sofiline.ro', category: 'Fashion', label: 'sofiline.ro',
    supply: 'stopped', supplied: true, subscribed: false, feedId: 21, catalogueId: null, productCount: 3210, programmeItemCount: 3210,
    sourceUrlDisplay: 'network.2performant.com/export.csv', sourceManaged: true, syncEnabled: false, lastSyncedAt: '2026-08-28T04:01:00Z', lastSyncError: 'Feed source responded with HTTP 404 (paused after 3 consecutive failures)', lastUploadAt: '2026-08-28T04:01:00Z', declaredAdvertiserHost: null,
    coversHosts: [], duplicateSupply: false, suppliedByFeedLabel: null,
    commissionRate: 12, cookieDays: 30, funded: true, programmeStatus: 'active', priorConfidence: 0.6,
    priorReasons: ['EPC at 55% of its network, shrunk for click volume at 40%.'],
    approvalAmountPct: 80, approvalCountPct: 82, epcRank: 180, epcRankOf: 507, epcValue: null, epcCurrency: null, paymentDays: 30, locksInWindow: 0, lockWindowDays: 90 },
  // Subscribed, catalogue still building. Removable — the old picker greyed this
  // button out permanently because it resolved ids through the servable list.
  // Host is resolver-shaped: `store.irinareisler.ro` folds to this.
  { key: '2performant:9899', network: '2performant', programmeKey: '9899', advertiserHost: 'irinareisler.ro', name: 'store.irinareisler.ro', category: 'Beauty', label: 'irinareisler.ro',
    supply: 'preparing', supplied: true, subscribed: true, feedId: null, catalogueId: 6, productCount: null, programmeItemCount: 969,
    sourceUrlDisplay: null, sourceManaged: false, syncEnabled: null, lastSyncedAt: null, lastSyncError: null, lastUploadAt: null, declaredAdvertiserHost: null,
    coversHosts: [], duplicateSupply: false, suppliedByFeedLabel: null,
    commissionRate: 10, cookieDays: 30, funded: false, programmeStatus: 'active', priorConfidence: 0.8,
    priorReasons: ['EPC at 94% of its network, shrunk for click volume at 50%.', 'Out of prepaid budget, so a share of clicks earn nothing.'],
    approvalAmountPct: 94, approvalCountPct: 96, epcRank: 32, epcRankOf: 507, epcValue: null, epcCurrency: null, paymentDays: 26, locksInWindow: 11, lockWindowDays: 90 },
  // A feed covering several advertisers is one row, never split per host.
  { key: 'feed:18', network: '2performant', programmeKey: null, advertiserHost: null, name: null, category: null, label: 'various-brands',
    supply: 'active', supplied: true, subscribed: false, feedId: 18, catalogueId: null, productCount: 4102, programmeItemCount: null,
    sourceUrlDisplay: null, sourceManaged: false, syncEnabled: false, lastSyncedAt: null, lastSyncError: null, lastUploadAt: '2026-08-30T11:20:00Z', declaredAdvertiserHost: null,
    coversHosts: ['gryxx.ro', 'tezyo.ro', 'zappatos.ro'], duplicateSupply: false, suppliedByFeedLabel: null,
    commissionRate: null, cookieDays: null, funded: null, programmeStatus: null, priorConfidence: 0, priorReasons: [],
    approvalAmountPct: null, approvalCountPct: null, epcRank: null, epcRankOf: null, epcValue: null, epcCurrency: null, paymentDays: null, locksInWindow: null, lockWindowDays: 90 },
  // Addable: accepted by the network, nothing supplies them.
  { key: '2performant:2754', network: '2performant', programmeKey: '2754', advertiserHost: 'decathlon.ro', name: 'decathlon.ro', category: 'Sport', label: 'decathlon.ro',
    supply: 'none', supplied: false, subscribed: false, feedId: null, catalogueId: null, productCount: null, programmeItemCount: 41022,
    sourceUrlDisplay: null, sourceManaged: false, syncEnabled: null, lastSyncedAt: null, lastSyncError: null, lastUploadAt: null, declaredAdvertiserHost: null,
    coversHosts: [], duplicateSupply: false, suppliedByFeedLabel: null,
    commissionRate: 6, cookieDays: 30, funded: true, programmeStatus: 'active', priorConfidence: 0.8,
    priorReasons: ['EPC at 72% of its network, shrunk for click volume at 88%.'],
    approvalAmountPct: 88, approvalCountPct: 90, epcRank: 140, epcRankOf: 507, epcValue: null, epcCurrency: null, paymentDays: 45, locksInWindow: 0, lockWindowDays: 90 },
  { key: '2performant:4825', network: '2performant', programmeKey: '4825', advertiserHost: 'librariadelfin.ro', name: 'librariadelfin.ro', category: 'Books', label: 'librariadelfin.ro',
    supply: 'none', supplied: false, subscribed: false, feedId: null, catalogueId: null, productCount: null, programmeItemCount: 236696,
    sourceUrlDisplay: null, sourceManaged: false, syncEnabled: null, lastSyncedAt: null, lastSyncError: null, lastUploadAt: null, declaredAdvertiserHost: null,
    coversHosts: [], duplicateSupply: false, suppliedByFeedLabel: null,
    commissionRate: 9, cookieDays: 10, funded: true, programmeStatus: 'active', priorConfidence: 0.8,
    priorReasons: ['EPC at 25% of its network, shrunk for click volume at 80%.'],
    approvalAmountPct: 60, approvalCountPct: 66, epcRank: 380, epcRankOf: 507, epcValue: null, epcCurrency: null, paymentDays: 60, locksInWindow: 0, lockWindowDays: 90 },
];

// Deterministic-ish 30/90-day mock series so the analytics charts have
// something to render without a backend.
function mockDailySeries(days: number): AnalyticsDailyPoint[] {
  const points: AnalyticsDailyPoint[] = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    const wave = Math.round(6 + 5 * Math.sin(i / 4));
    points.push({
      day: d.toISOString().slice(0, 10),
      clicks: Math.max(0, wave - 3 + (i % 3)),
      matchesServed: Math.max(0, wave * 8 + (i % 5) * 3),
      // Views sit between serves and clicks (visible share of delivered overlays)
      views: Math.max(0, wave * 3 + (i % 4) * 2),
      // Impressions: pageview-level traffic baseline above serves
      impressions: Math.max(0, wave * 12 + (i % 6) * 4),
    });
  }
  return points;
}

const mockRecentClicks: AnalyticsClickLedgerEntry[] = [
  { clickRef: 'ia_9k2m7p1qa2', feedId: 1, feedLabel: 'outdoor-gear', network: 'awin', createdAt: '2026-07-13T18:22:00Z' },
  { clickRef: 'ia_x3n8v0w4b7', feedId: 1, feedLabel: 'outdoor-gear', network: 'awin', createdAt: '2026-07-13T14:05:00Z' },
  // Amazon: click-ref injection is policy-disabled (see redirect.ts's
  // SUBTAG_DIALECT) — nothing is ever transmitted to reconcile against, so
  // a real Amazon click never carries a click_ref.
  { clickRef: null, feedId: 3, feedLabel: 'default', network: 'amazon', createdAt: '2026-07-12T21:40:00Z' },
  { clickRef: 'ia_q7w4e2r8d1', feedId: 2, feedLabel: 'home-decor', network: 'cj', createdAt: '2026-07-12T09:11:00Z' },
];

// Flag keys mock mode grants — see mockFlags.ts for why the default is empty.
const mockFlags = parseMockFlags(process.env.REACT_APP_MOCK_FLAGS);

// Pipeline faults mock mode reports — see mockFaults.ts. Empty by default, so a
// mock account is healthy unless asked otherwise; the point is that an UNHEALTHY
// one is now reachable at all, which it was not.

// THE VERIFIED STATE WAS UNREACHABLE IN MOCK MODE, PORTAL-WIDE. The profile
// fixture hardcoded `verificationStatus: 'pending'`, so `npm run dev:user` could
// only ever show a pre-activation account: the dashboard's WebsiteVerification
// block always rendered, the activation checklist was always stuck on step 1,
// and the install page always wore its amber "this key will not serve matches"
// warning. Every screen assessment to date graded that one state — and it is the
// state a publisher leaves within a day and never returns to.
//
// Same shape as REACT_APP_MOCK_FAULTS and REACT_APP_MOCK_FLAGS, and the default
// is unchanged for the same reason: a fresh mock account is pending, because
// that is what a fresh real account is. This makes the OTHER state reachable.
//
//   REACT_APP_MOCK_VERIFIED=true npm run dev:user
const mockVerified = process.env.REACT_APP_MOCK_VERIFIED === 'true';
const MOCK_VERIFICATION_STATUS = mockVerified ? 'verified' : 'pending';
const mockFaults = parseMockFaults(process.env.REACT_APP_MOCK_FAULTS);

// How many shops the fixture serves. Unset leaves the hand-shaped 9 alone; a
// number repeats them, which is what makes the page's DEPTH measurable at the
// live account's size. See `mockScale.ts` for why a small fixture is its own
// blind spot.
const mockScale = parseMockScale(process.env.REACT_APP_MOCK_SCALE);

/**
 * One inspected feed, in both the states the review table can be in.
 *
 * `autoResolved` decides which of two panels renders: "Columns recognised"
 * (confirm the link and go) or "Check your columns" (every unresolved field
 * needs a dropdown set). The second carries far more controls and is the one a
 * publisher is most likely to get stuck on, so it must be reachable on its own —
 * `REACT_APP_MOCK_FAULTS=unmapped`.
 */
function mockInspection() {
  const headers = [
    'product_id', 'product_name', 'long_description', 'image_url', 'deep_link',
    'sale_price', 'regular_price', 'brand', 'category', 'merchant_name', 'currency',
  ];
  const link = 'https://event.2performant.com/events/click?ad_type=quicklink'
    + '&aff_code=abc123def456&unique=9f8e7d6c&redirect_to='
    + 'https%3A%2F%2Fshop.example.com%2Fp%2F88213-rochie-eleganta-midi';
  const image = 'https://cdn.example-merchant.com/media/catalog/product/cache/'
    + '1a2b3c4d5e6f/r/o/rochie-eleganta-midi-88213.jpg';
  const samples: Record<string, string> = {
    id: 'SKU-88213-XL',
    name: 'Rochie eleganta midi cu maneca lunga si imprimeu floral pentru ocazii speciale',
    description: 'Confectionata din material premium cu captuseala completa si fermoar ascuns lateral',
    image,
    link,
    price: '249.99',
  };
  const full = {
    id: 'product_id', name: 'product_name', description: 'long_description',
    image: 'image_url', link: 'deep_link', price: 'sale_price',
  };

  if (mockFaults.includes('unmapped')) {
    // The link and the image are exactly the two a network vocabulary most often
    // fails to name, and the link is the one whose wrong column sends clicks that
    // earn nothing — which is why the form always makes it confirm.
    const { link: _l, image: _i, ...resolved } = full;
    return {
      success: true, autoResolved: false, headers,
      sampleRows: [Object.values(samples)],
      mapping: resolved,
      samples: { ...samples, link: '', image: '' },
      unresolved: ['image', 'link'],
      mustConfirm: ['link'],
    };
  }
  return {
    success: true, autoResolved: true, headers,
    sampleRows: [Object.values(samples)],
    mapping: full, samples, unresolved: [], mustConfirm: ['link'],
  };
}


const MOCK_PAUSED_FEED = {
  id: 1,
  label: 'outdoor-gear',
  network: 'awin',
  lastError: 'Feed source responded with HTTP 404',
  lastSyncedAt: '2026-09-01T04:12:00Z',
};
const MOCK_STALE_UPLOAD = {
  id: 2,
  label: 'home-decor',
  network: 'cj',
  lastUploadAt: '2026-08-14T11:30:00Z',
};
const MOCK_BROKEN_CONNECTION = { id: 2, network: 'awin', accountRef: '123456' };

// Route mock responses by method + path
const mockRoutes: Record<string, (url: URL, init?: RequestInit) => unknown> = {
  'POST /api/auth/signup': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    return {
      success: true,
      message: 'Account created successfully',
      user: { id: 1, email: body.email, name: '', role: 'user', status: 'active' },
      tokens: { accessToken: 'mock-jwt-token-user', refreshToken: 'mock-refresh-token', expiresIn: 3600 },
    };
  },
  'POST /api/auth/login': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    return {
      user: { id: 1, email: body.email, name: 'Test User', role: 'user', status: 'active' },
      tokens: { accessToken: 'mock-jwt-token-user', refreshToken: 'mock-refresh-token' },
    };
  },
  'POST /api/auth/forgot-password': () => ({
    success: true,
    message: 'If an account exists for that email, a reset link has been sent.',
  }),
  // `token=expired` exercises the used/expired branch without a backend — the
  // path a real visitor is most likely to hit, since email clients prefetch.
  'POST /api/audit/session': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    if (body.token === 'expired') {
      throw new Response(
        JSON.stringify({ success: false, message: 'That link has already been used or has expired. Request a new report to get a fresh one.' }),
        { status: 401, headers: { 'content-type': 'application/json' } }
      );
    }
    return {
      success: true,
      reportId: 99,
      // pending, because an audit proves nothing about who owns the site
      user: { id: 1, email: 'audited@example.com', role: 'user', status: 'pending' },
      tokens: { accessToken: 'mock-jwt-token-user', refreshToken: 'mock-refresh-token' },
    };
  },
  'GET /api/audit/report/{id}': (url) => ({
    success: true,
    report: {
      id: Number(url.pathname.split('/').pop()) || 99,
      url: 'https://blog.example/style/long-hair-cuts/',
      title: 'Long hair cuts',
      createdAt: '2026-07-25T09:00:00Z',
      images: 48,
      missingDescription: 6,
      distinctDescriptions: 47,
      linkedToAttachmentPage: 43,
      linkedToImageFile: 0,
      topics: ['style', 'long hair', 'cuts'],
      subjects: [
        { subject: 'Hair care and styling', images: 28 },
        { subject: 'Hair colouring', images: 9 },
        { subject: 'Styling tools', images: 5 },
      ],
      remainder: 6,
    },
  }),
  'POST /api/auth/reset-password': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    if (!body.token || body.token === 'expired') {
      throw new Response(
        JSON.stringify({ success: false, message: 'This reset link is invalid or has expired. Please request a new one.' }),
        { status: 400, headers: { 'content-type': 'application/json' } }
      );
    }
    return { success: true, message: 'Password updated. You can now log in with your new password.' };
  },
  'GET /api/auth/profile': () => ({
    success: true,
    user: { id: 1, email: 'user@example.com', name: 'Test User', role: 'user', status: 'active' },
    profile: { firstName: 'Test', lastName: 'User', company: 'Acme', websiteUrl: 'https://example.com', verificationStatus: MOCK_VERIFICATION_STATUS },
    // feature-flag-registry: empty unless REACT_APP_MOCK_FLAGS names keys —
    // see mockFlags above for why the default stays empty.
    flags: mockFlags,
  }),
  'PUT /api/auth/profile': () => ({
    success: true,
    message: 'Profile updated successfully',
    profile: { firstName: 'Test', lastName: 'User', company: 'Acme', websiteUrl: 'https://example.com', verificationStatus: MOCK_VERIFICATION_STATUS },
  }),
  'GET /api/auth/verification': () => ({
    success: true,
    verification: {
      token: 'mock-verification-token-abc123',
      filename: 'imgaff-verify-mock-verification-token-abc123.txt',
      content: 'imgaff-verification: mock-verification-token-abc123\nwebsite: https://example.com\ngenerated: 2026-04-07T00:00:00.000Z',
      instructions: '1. Create a file named "imgaff-verify-mock-verification-token-abc123.txt" with the content below\n2. Upload it to your website root directory\n3. Click "Verify Website" to complete verification',
    },
  }),
  'POST /api/auth/verify-website': () => ({
    success: true,
    message: 'Website verification successful! Your account is now active.',
  }),
  // THE COLUMN-REVIEW TABLE, REACHABLE AT LAST.
  //
  // Both inspect endpoints used the global `fetch` and so ignored mock mode
  // entirely — they answered 404 against an API that is not running, and the
  // table they gate could not be rendered here at ALL. Two dated verdicts on the
  // Feeds screen carried it as "unverified" as a result.
  //
  // THE SAMPLES ARE LONG ON PURPOSE. This table's cells are `whiteSpace: nowrap`
  // and its sample column is capped at `16rem` with an ellipsis, so whether it
  // survives a 390px viewport depends entirely on how long the values are. A
  // fixture with tidy short samples renders a narrow table and would let a
  // reviewer conclude "no problem at 390" from a case that cannot show one —
  // which is the same failure this whole change exists to fix, one level down.
  // These are the shape of a real affiliate feed: a 190-character deep link and
  // a CDN image URL.
  'POST /api/affiliate-feeds/inspect': () => mockInspection(),
  'POST /api/affiliate-feeds/inspect-url': () => mockInspection(),

  'POST /api/affiliate-feeds/upload': (_url, init) => {
    const formData = init?.body as FormData | undefined;
    const network = formData?.get('network') || 'unknown';
    const label = String(formData?.get('label') || 'default');
    return {
      success: true,
      message: `Successfully processed 42 products from ${network} feed`,
      result: {
        feedId: `mock-feed-${Date.now()}`,
        network: String(network),
        totalRows: 45,
        totalProducts: 45,
        processedProducts: 42,
        skippedProducts: 3,
        processingTime: 1234,
        errors: ['2 row(s): missing affiliate link'],
        feedLabel: label,
        replaced: label === 'default',
        aborted: false,
        rowIssuesTruncated: false,
        issues: [
          { reason: 'affiliate link is not a valid URL (must start with http:// or https://)', severity: 'error', count: 2, sampleRows: [12, 28] },
          { reason: 'name longer than 500 characters was truncated', severity: 'warning', count: 1, sampleRows: [41] },
        ],
        rowIssues: [
          { row: 12, severity: 'error', reason: 'affiliate link is not a valid URL (must start with http:// or https://)' },
          { row: 28, severity: 'error', reason: 'affiliate link is not a valid URL (must start with http:// or https://)' },
          { row: 41, severity: 'warning', reason: 'name longer than 500 characters was truncated' },
        ],
      },
    };
  },
  'GET /api/affiliate-feeds/feeds': () => ({
    success: true,
    feeds: mockFeeds,
  }),
  'POST /api/affiliate-feeds/feeds': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    const id = Math.max(0, ...mockFeeds.map(f => f.id)) + 1;
    const label = body.label || 'default';
    mockFeeds.push({
      id, network: body.network, label, productCount: 0,
      createdAt: new Date().toISOString(), lastUploadAt: null,
      epcEstimate: null, epcCurrency: null,
      sourceUrlDisplay: redactMockUrl(body.sourceUrl), syncEnabled: true,
      lastSyncedAt: null, lastSyncStatus: null, lastSyncError: null,
    });
    return { success: true, feed: { id, network: body.network, label } };
  },
  'DELETE /api/affiliate-feeds/feeds/{id}': (url) => {
    const id = url.pathname.split('/').pop();
    return { success: true, message: `Deleted feed ${id} and 42 products`, deletedCount: 42 };
  },
  'PATCH /api/affiliate-feeds/feeds/{id}': (url, init) => {
    const id = Number(url.pathname.split('/').pop());
    const body = JSON.parse((init?.body as string) || '{}');
    const feed = mockFeeds.find(f => f.id === id);
    const result: Record<string, unknown> = { id };
    if (!feed) return { success: false, message: 'Feed not found' };

    if ('epcEstimate' in body || 'epcCurrency' in body) {
      feed.epcEstimate = body.epcEstimate;
      feed.epcCurrency = body.epcCurrency;
      result.epcEstimate = body.epcEstimate;
      result.epcCurrency = body.epcCurrency;
    }
    if ('sourceUrl' in body) {
      feed.sourceUrlDisplay = redactMockUrl(body.sourceUrl);
      feed.syncEnabled = Boolean(body.sourceUrl);
      feed.lastSyncStatus = null;
      feed.lastSyncError = null;
      result.sourceUrlDisplay = feed.sourceUrlDisplay;
      result.syncEnabled = feed.syncEnabled;
    }
    if ('syncEnabled' in body) {
      feed.syncEnabled = body.syncEnabled;
      result.syncEnabled = feed.syncEnabled;
    }
    if ('declaredAdvertiserHost' in body) {
      feed.declaredAdvertiserHost = body.declaredAdvertiserHost;
      result.declaredAdvertiserHost = feed.declaredAdvertiserHost;
    }
    return { success: true, feed: result };
  },
  'GET /api/affiliate-feeds/feeds/{id}/suggest-advertiser': (url) => {
    const id = Number(url.pathname.split('/')[4]);
    const feed = mockFeeds.find(f => f.id === id);
    if (!feed) return { success: false, message: 'Feed not found' };
    // No real product data to sample in mock mode — mirrors the "we saw
    // nothing" case so the UI's empty state is exercisable locally.
    return { success: true, suggestion: null, distinctValues: [] };
  },
  'POST /api/affiliate-feeds/feeds/{id}/sync': (url) => {
    const id = Number(url.pathname.split('/')[3]);
    const feed = mockFeeds.find(f => f.id === id);
    if (feed) {
      feed.lastSyncedAt = new Date().toISOString();
      feed.lastSyncStatus = 'ok';
      feed.lastSyncError = null;
    }
    return {
      success: true,
      message: `Successfully processed 12 products from ${feed?.network ?? 'unknown'} feed`,
      result: {
        feedId: `mock-sync-${Date.now()}`, feedEntityId: id, network: feed?.network ?? 'unknown',
        totalRows: 12, totalProducts: 12, processedProducts: 12, skippedProducts: 0,
        processingTime: 640, errors: [], feedLabel: feed?.label ?? 'default', replaced: true,
        aborted: false, rowIssuesTruncated: false, issues: [], rowIssues: [],
      },
    };
  },
  // ONE COHERENT ACCOUNT, and that is the point rather than a nicety.
  //
  // The four feeds fixtures used to name four DISJOINT sets of shops on four
  // disjoint networks — picker on 2performant, feeds on awin/cj/amazon, history
  // on other names again. Each was honest alone and they were collectively
  // impossible, so mock mode rendered four small unrelated lists and the defect
  // that made the real page unreadable — the same shop in every list, with an
  // `Add` button for shops the publisher already sold from — could not occur.
  // A UX pass run against mocks therefore could not see it.
  //
  // Every fixture below describes the SAME account: `mockShops`, `mockFeeds`,
  // `mockFeedHistory` and `mockFeedStats` name the same shops on 2performant,
  // and the states a publisher actually reaches are all present — struck out by
  // the scheduler, paused on purpose, failing but retrying, still preparing, a
  // multi-advertiser feed, and one shop supplied twice.
  //
  // HOSTS ARE RESOLVER-SHAPED. `hostishToRegistrableDomain` folds
  // `store.irinareisler.ro` to `irinareisler.ro`, so a fixture using the long
  // form would let the join pass in mock mode and fail against real data.
  'GET /api/user/shops': () => ({
    consent:
      'Adding a shop creates a product export inside your own affiliate-network account, '
      + 'named so you can find it. You can delete it there at any time; removing it here '
      + 'stops us refreshing it.',
    shops: scaleShops(
      mockFaults.includes('repairing')
        ? mockShops.map(sh => (sh.supply === 'stopped' && sh.sourceManaged ? { ...sh, supply: 'repairing' as const } : sh))
        : mockShops,
      mockScale),
  }),

  // Shaped from the live account, so the three cases the row must tell apart all
  // appear: a shop that converts, one that is shown constantly and barely
  // clicked, and one shown enough to judge and never clicked at all. On the real
  // data 20 of 29 feeds are in that last group.
  'GET /api/user/shops/outcome': () => ({
    windowDays: 30,
    minShowsToJudge: 20,
    rows: [
      { feedId: 8,  shows: 96,   clicks: 20, shownNeverClicked: false },
      { feedId: 12, shows: 1289, clicks: 11, shownNeverClicked: false },
      { feedId: 11, shows: 981,  clicks: 0,  shownNeverClicked: true },
      { feedId: 9,  shows: 3,    clicks: 0,  shownNeverClicked: false },
    ],
  }),

  'POST /api/user/catalogues/subscribe': () => ({
    success: true, catalogueId: 3, state: 'preparing',
  }),
  'DELETE /api/user/catalogues/:id': () => ({ success: true, changed: true }),
  'GET /api/user/api-key': () => ({ success: true, apiKey: 'imgaff_example_com_abc123def456' }),
  'GET /api/user/catalog-coverage': () => ({
    success: true,
    status: 'ok',
    windowDays: 30,
    requests: 4619,
    matched: 3395,
    missedNoDescription: 352,
    missedNoProducts: 872,
    coveragePercent: 74,
    uncoveredGroups: [
      { subject: 'Tunsori', images: 104 },
      { subject: 'Tinute elegante', images: 58 },
      { subject: 'Sandale', images: 49 },
      { subject: 'Tinute de club', images: 26 },
    ],
    uncoveredRemainder: 635,
    computedAt: new Date().toISOString(),
  }),

  'GET /api/user/analytics': (url) => {
    const periodParam = url.searchParams.get('period');
    const legacyDays = url.searchParams.get('days') === '90' ? 90 : 30;
    const periods: Record<string, { period: string; days: number }> = {
      today: { period: 'today', days: 1 }, yesterday: { period: 'yesterday', days: 1 },
      week: { period: 'week', days: 4 }, '30d': { period: '30d', days: 30 }, '90d': { period: '90d', days: 90 },
    };
    const resolved = (periodParam && periods[periodParam]) || { period: legacyDays === 90 ? '90d' : '30d', days: legacyDays };
    // `no-data` is the first-run account: the widget has reported nothing yet.
    // Zeroed here rather than with an empty `daily`, because a real new account
    // HAS days — they are just all zero — and an empty array takes a different
    // branch ("No data yet." on the chart) that no publisher with a live widget
    // ever sees.
    const daily = mockFaults.includes('no-data')
      ? mockDailySeries(resolved.days).map(d => ({ ...d, clicks: 0, matchesServed: 0, views: 0, impressions: 0 }))
      : mockDailySeries(resolved.days);
    const totalClicks = daily.reduce((sum, d) => sum + d.clicks, 0);
    const totalViews = daily.reduce((sum, d) => sum + d.views, 0);
    const totalImpressions = daily.reduce((sum, d) => sum + (d.impressions ?? 0), 0);
    const totalMatches = daily.reduce((sum, d) => sum + d.matchesServed, 0);
    // Deterministic-ish previous-period comparison (~85% of current)
    const comparison = {
      matches: Math.round(totalMatches * 0.85), clicks: Math.round(totalClicks * 0.85),
      views: Math.round(totalViews * 0.85), impressions: Math.round(totalImpressions * 0.85),
    };

    const perFeed = mockFeeds.map((f, i) => {
      const clicks = [23, 9, 5][i] ?? 4;
      const estimatedEarnings = f.epcEstimate != null ? Math.round(clicks * f.epcEstimate * 100) / 100 : null;
      return {
        feedId: f.id, label: f.label, network: f.network, clicks,
        epcEstimate: f.epcEstimate ?? null, epcCurrency: f.epcCurrency ?? null, estimatedEarnings,
      };
    });

    const configuredFeeds = perFeed.filter(f => f.epcEstimate != null && f.epcCurrency != null).length;
    const excludedFeeds = perFeed.length - configuredFeeds;
    const totalsByCurrency = new Map<string, number>();
    for (const f of perFeed) {
      if (f.epcEstimate != null && f.epcCurrency != null) {
        totalsByCurrency.set(f.epcCurrency, (totalsByCurrency.get(f.epcCurrency) || 0) + (f.estimatedEarnings || 0));
      }
    }
    const estimates = Array.from(totalsByCurrency.entries())
      .map(([currency, total]) => ({ currency, total: Math.round(total * 100) / 100, configuredFeeds, excludedFeeds }))
      .sort((a, b) => a.currency.localeCompare(b.currency));

    return {
      success: true,
      period: resolved.period,
      // Mirrors the real response so the timezone note renders under mocks too
      // — a note that only appears against the live API is a note nobody sees
      // while developing the page.
      timezone: 'UTC',
      totalMatches,
      totalClicks,
      totalViews,
      totalImpressions,
      viewTrackingStartedAt: '2026-07-19T12:00:00Z',
      // BOTH STATES REACHABLE. `partial` is what a young account sees on every
      // period — the previous window predating its data — and it is the state the
      // live account was in when the artifact was measured. Default false so the
      // badges (the steady state) stay the mock's default; `REACT_APP_MOCK_FAULTS=
      // no-baseline` gives the other one, for the same reason `unmapped` exists.
      comparison: { ...comparison, partial: mockFaults.includes('no-baseline'), comparableSince: '2026-07-19' },
      // TWO LISTS, AND THE SECOND ONE COULD NOT BE RENDERED HERE AT ALL.
      //
      // `rankProducts` splits per-product counts into "Most clicked" and "Shown
      // often, never clicked", and the API serves both. This fixture carried
      // `topProducts` only, so the second list — the one the backlog called "the
      // more actionable one", and the single place this read-only screen produces
      // a finding and points at Product feeds — was UNREACHABLE in mock mode.
      // Every assessment of `/analytics` runs in mock mode. Its unit tests cover
      // the branch and pass; nobody had ever looked at it. That is the
      // `FeedHealthBanner` lesson again: a branch that is tested and unviewable.
      //
      // SHAPED FROM THE LIVE ACCOUNT, 30 days, measured 2026-09-07 — because a
      // tidy fixture is what let the old one pass review:
      //
      //   967 products matched or clicked
      //     7 have ANY click, and the best has 1 click from 772 shows
      //    96 qualify as shown-but-never-clicked; the top one 1,160 shows
      //   name lengths run to 67 characters
      //
      // So the honest success list is five rows all reading "1 clicks", which is
      // what a publisher at this volume actually sees, and the honest failure list
      // is four figures deep. The old fixture's 7-to-23 clicks described an
      // account nobody has.
      //
      // EVERY ROW CARRIES A URL, deliberately. The rows render as links only when
      // one is present, and no fixture row had one — so the inert-text state that
      // R1 shipped a fix for was the only state mock mode could show.
      topProducts: mockFaults.includes('no-data') ? [] : [
        { name: 'Fixativ Spray Extra Strong 400ml', url: 'https://event.2performant.com/events/click?ad_type=quicklink&aff_code=abc123&unique=1a2b&redirect_to=https%3A%2F%2Fshop.example.ro%2Fp%2F41221', matches: 772, clicks: 1 },
        { name: 'Pantofi eleganti dama, piele naturala, verde', url: 'https://event.2performant.com/events/click?ad_type=quicklink&aff_code=abc123&unique=2b3c&redirect_to=https%3A%2F%2Fshop.example.ro%2Fp%2F41222', matches: 41, clicks: 1 },
        { name: 'Pensula fard de ochi', url: 'https://event.2performant.com/events/click?ad_type=quicklink&aff_code=abc123&unique=3c4d&redirect_to=https%3A%2F%2Fshop.example.ro%2Fp%2F41223', matches: 40, clicks: 1 },
        { name: 'Fusta din tulle lunga, albastra', url: 'https://event.2performant.com/events/click?ad_type=quicklink&aff_code=abc123&unique=4d5e&redirect_to=https%3A%2F%2Fshop.example.ro%2Fp%2F41224', matches: 16, clicks: 1 },
        { name: 'Rochie eleganta midi', url: 'https://event.2performant.com/events/click?ad_type=quicklink&aff_code=abc123&unique=5e6f&redirect_to=https%3A%2F%2Fshop.example.ro%2Fp%2F41225', matches: 2, clicks: 1 },
      ],
      neverClicked: mockFaults.includes('no-data') ? [] : [
        { name: 'Fixativ Organic Pure Normal', url: 'https://event.2performant.com/events/click?ad_type=quicklink&aff_code=abc123&unique=6f7g&redirect_to=https%3A%2F%2Fshop.example.ro%2Fp%2F51221', matches: 1160, clicks: 0 },
        { name: 'Spuma de par pentru volum, gama profesionala, 250 ml', url: 'https://event.2performant.com/events/click?ad_type=quicklink&aff_code=abc123&unique=7g8h&redirect_to=https%3A%2F%2Fshop.example.ro%2Fp%2F51222', matches: 936, clicks: 0 },
        { name: 'Spuma de par Normal', url: 'https://event.2performant.com/events/click?ad_type=quicklink&aff_code=abc123&unique=8h9i&redirect_to=https%3A%2F%2Fshop.example.ro%2Fp%2F51223', matches: 902, clicks: 0 },
        { name: 'Vopsea de par Oleo Intense 8-60 Honey Blond', url: 'https://event.2performant.com/events/click?ad_type=quicklink&aff_code=abc123&unique=9i0j&redirect_to=https%3A%2F%2Fshop.example.ro%2Fp%2F51224', matches: 598, clicks: 0 },
        { name: 'Vopsea de par Age Perfect 9.31 Light Sand Blond', url: 'https://event.2performant.com/events/click?ad_type=quicklink&aff_code=abc123&unique=0j1k&redirect_to=https%3A%2F%2Fshop.example.ro%2Fp%2F51225', matches: 505, clicks: 0 },
      ],
      daily,
      perFeed,
      estimates,
      recentClicks: mockRecentClicks,
    };
  },
  'GET /api/user/usage': () => {
    const periodStart = new Date(Date.UTC(2026, 0, 10));
    const periodEnd = new Date(Date.UTC(2027, 0, 10));
    return {
      success: true,
      plan: 'free',
      allowance: 100000,
      used: 12000,
      periodStart: periodStart.toISOString(),
      periodEnd: periodEnd.toISOString(),
      completeForPeriod: true,
    };
  },
  'GET /api/user/setup-status': () => ({
    success: true,
    feedUploaded: true,
    widgetSeen: true,
    firstMatch: false,
    // The measurement step, deliberately unset in the mock: it is the OPTIONAL
    // one, and the checklist must look right with it open.
    conversionSourceConnected: false,
    conversionMeasured: false,
    // Healthy unless REACT_APP_MOCK_FAULTS asks otherwise. The banner renders
    // nothing for empty arrays, so the default account is unchanged.
    pausedFeeds: mockFaults.includes('paused') ? [MOCK_PAUSED_FEED] : [],
    staleUploads: mockFaults.includes('stale') ? [MOCK_STALE_UPLOAD] : [],
    staleUploadDays: 7,
    brokenConnections: mockFaults.includes('broken') ? [MOCK_BROKEN_CONNECTION] : [],
  }),
  'GET /api/user/conversions/connections': () => ({
    success: true,
    connections: [
      { id: 1, network: '2performant', credentialKind: 'none', syncState: 'never_connected', lastSyncedAt: null, feedCount: 2 },
      { id: 2, network: 'awin', credentialKind: 'token', syncState: 'active', lastSyncedAt: new Date().toISOString(), feedCount: 1 },
    ],
  }),
  'GET /api/user/conversions/readiness': () => ({
    success: true,
    networks: [
      { connectionId: 1, network: '2performant', clicks: 84, state: 'expected', dismissible: false, dismissed: false },
      { connectionId: 2, network: 'awin', clicks: 12, state: 'satisfied', dismissible: true, dismissed: false },
    ],
  }),
  'GET /api/user/conversions/earnings': () => ({
    success: true,
    // Two currencies and two tiers deliberately: the panel must render them as
    // separate figures, never a combined total.
    measured: [
      { feedId: 2, currency: 'EUR', status: 'approved', commission: 14.6, sales: 412.5, conversions: 3 },
      { feedId: 2, currency: 'EUR', status: 'pending', commission: 4.8, sales: 96.0, conversions: 1 },
      { feedId: 1, currency: 'RON', status: 'pending', commission: 36.2, sales: 890.0, conversions: 2 },
    ],
    unattributed: [
      { attribution: 'matched', count: 6 },
      { attribution: 'no_ref', count: 2 },
    ],
    byNetwork: [
      {
        network: 'rakuten', attribution: 'no_ref', count: 2,
        explanation: 'Rakuten only keeps click data for 30 days, so conversions from older clicks arrive without our tag. They are real earnings and are counted here, but they cannot be traced back to a feed or image. Nothing is wrong with your setup.',
      },
    ],
    rate: {
      rate: 0.0214, withheld: null, matchedClicks: 6, attributableClicks: 280,
      windowDays: 30, floor: 100,
    },
    hasActiveConnection: true,
    lastSyncedAt: '2026-08-22T15:20:30.584Z',
  }),
  'POST /api/user/conversions/upload': () => ({
    success: true,
    uploadId: 7,
    report: {
      rowsRead: 42, created: 40, matched: 31, noRef: 6, unknownRef: 3, inherited: 0,
      appended: 40, unchanged: 0, skippedProvisional: 0, rejected: 2,
      rejections: [{ row: 12, reason: 'missing commission amount' }],
    },
  }),
  'POST /api/user/conversions/connections/{id}/dismiss': () => ({ success: true }),
  'POST /api/user/conversions/connections/{id}/credential': (_url, init) => {
    const body = JSON.parse((init?.body as string) || '{}');
    if (!body.credential) {
      throw new Response(
        JSON.stringify({ success: false, message: 'A network account and a credential are required.' }),
        { status: 400, headers: { 'content-type': 'application/json' } }
      );
    }
    return {
      success: true,
      synced: true,
      report: {
        rowsRead: 18, created: 18, matched: 14, noRef: 4, unknownRef: 0, inherited: 0,
        appended: 18, unchanged: 0, skippedProvisional: 0,
      },
      caveat: null,
    };
  },
  'POST /api/user/debug-token': () => {
    // Mirrors the API: the token binds to the account's registered domain,
    // never anything the caller supplies; the launch URL carries it in the
    // fragment only.
    const token = `mock-debug-token-${Date.now()}`;
    return {
      success: true,
      token,
      domain: 'example.com',
      url: `https://example.com/#imgaff_debug=${encodeURIComponent(token)}`,
      expiresIn: 900,
    };
  },
  // HONOURS `feedRef`, because a mock that ignores a filter shows the screen
  // working when it is not: the room would render every feed's runs and look
  // exactly like a correct per-feed history.
  'GET /api/affiliate-feeds/history': (url) => {
    const ref = url.searchParams.get('feedRef');
    const history = ref === null
      ? mockFeedHistory
      : mockFeedHistory.filter(h => String((h as any).feed_ref) === ref);
    return { success: true, history };
  },
  'GET /api/affiliate-feeds/stats': () => ({ success: true, stats: mockFeedStats }),
  'DELETE /api/affiliate-feeds/products/{network}': (url) => {
    const network = url.pathname.split('/').pop();
    return { success: true, message: `Deleted 42 products from ${network}`, deletedCount: 42 };
  },
};

// Exact key first, then parameterised routes ({network} → one path segment)
function matchMockRoute(method: string, pathname: string): string | undefined {
  const exact = `${method} ${pathname}`;
  if (mockRoutes[exact]) return exact;
  for (const key of Object.keys(mockRoutes)) {
    const [m, pattern] = key.split(' ', 2);
    if (m !== method || !pattern.includes('{')) continue;
    const re = new RegExp('^' + pattern.replace(/\{[^}]+\}/g, '[^/]+') + '$');
    if (re.test(pathname)) return key;
  }
  return undefined;
}

const mockFetch: typeof fetch = async (input, init) => {
  // openapi-fetch calls fetch(request) with a fully-built Request and no init,
  // so method and body must be read off the Request when init is absent
  const request = input instanceof Request ? input : null;
  const url = new URL(request ? request.url : String(input));
  const method = (init?.method || request?.method || 'GET').toUpperCase();
  const key = matchMockRoute(method, url.pathname);
  const handler = key ? mockRoutes[key] : undefined;
  if (!handler) {
    return new Response(JSON.stringify({ message: `No mock for ${method} ${url.pathname}` }), { status: 404, headers: { 'content-type': 'application/json' } });
  }
  let handlerInit = init;
  if (!handlerInit?.body && request && method !== 'GET' && method !== 'HEAD') {
    const contentType = request.headers.get('content-type') || '';
    handlerInit = {
      method,
      body: contentType.includes('multipart/form-data')
        ? ((await request.formData()) as unknown as BodyInit)
        : await request.text(),
    };
  }
  try {
    const data = handler(url, handlerInit);
    return new Response(JSON.stringify(data), { status: 200, headers: { 'content-type': 'application/json' } });
  } catch (err) {
    if (err instanceof Response) return err;
    throw err;
  }
};

// ---------------------------------------------------------------------------
// Create the openapi-fetch client
// ---------------------------------------------------------------------------

const useMocks = process.env.REACT_APP_USE_MOCKS === 'true';
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Real-mode fetch wrapper: on an explicit 401 HTTP response (never on network
// errors, which throw before a response exists), notify AuthContext — the
// single owner of stored auth state — so it can clear it and log the user out.
//
// Exception: /api/audit/session answers 401 for a link that was already used
// or has expired. That is a normal outcome of redeeming a token, not a stale
// session — treating it as one would log out a signed-in user for clicking an
// old email.
const realFetch: typeof fetch = async (input, init) => {
  const response = await fetch(input, init);
  const url = typeof input === 'string' ? input : (input as Request).url ?? String(input);
  if (response.status === 401 && !url.includes('/api/audit/session')) {
    window.dispatchEvent(new Event('ezaff:unauthorized'));
  }
  return response;
};

/**
 * The one fetch this module makes requests with, mock or real.
 *
 * NAMED AND SHARED BECAUSE TWO CALLS CANNOT GO THROUGH `client`. The feed
 * inspect endpoints post multipart form data and are hand-rolled; until now
 * they called the global `fetch` directly, which meant they were the ONLY two
 * methods here that ignored `REACT_APP_USE_MOCKS`. In mock mode they reached
 * for a real API that is not running and answered 404.
 *
 * What that cost is not a broken dev server — it is a surface NOBODY HAS EVER
 * ASSESSED. Both feed-supply paths that involve a file (upload, and connect by
 * URL) render the column-review table only after an inspect succeeds, so in
 * mock mode that table could not be rendered at all. Two dated verdicts on the
 * Feeds screen recorded it as "unverified" without anyone establishing that it
 * was in fact UNREACHABLE — the same shape as the healthy-account fixture that
 * hid `FeedHealthBanner`, and the reason `mockFaults` exists.
 *
 * A new hand-rolled call must use this, never the global `fetch`.
 */
const appFetch: typeof fetch = useMocks ? mockFetch : realFetch;

// User-api paths already include /api/ prefix, so baseUrl is just the host
const client = createClient<paths>({
  baseUrl: apiUrl,
  fetch: appFetch,
});

// ---------------------------------------------------------------------------
// Wrapper: same method surface components already consume
// ---------------------------------------------------------------------------

function getToken(): string | null {
  return localStorage.getItem('accessToken');
}

function authHeaders(): HeadersInit {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const apiService = {
  async signup(email: string, password: string, websiteUrl?: string): Promise<LoginResponse> {
    const { data, error } = await client.POST('/api/auth/signup', {
      body: { email, password, websiteUrl, attribution: collectAttribution() },
    });
    if (error) throw new Error((error as any).message || 'Signup failed');
    return { token: data!.tokens.accessToken, user: data!.user };
  },

  async getProfile() {
    const { data, error } = await client.GET('/api/auth/profile', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch profile');
    return data!;
  },

  async getVerificationInfo() {
    const { data, error } = await client.GET('/api/auth/verification', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch verification info');
    return data!;
  },

  async verifyWebsite() {
    const { data, error } = await client.POST('/api/auth/verify-website', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Website verification failed');
    return data!;
  },

  async login(email: string, password: string): Promise<LoginResponse> {
    const { data, error } = await client.POST('/api/auth/login', { body: { email, password } });
    if (error) throw new Error((error as any).message || 'Login failed');
    // Normalize: spec returns { tokens: { accessToken, refreshToken }, user }
    // Components expect { token, user }
    return { token: data!.tokens.accessToken, user: data!.user };
  },

  // Redeem an audit magic link. Returns the same { token, user } shape as
  // login so AuthContext installs an ordinary session from it, plus the report
  // the link was issued for.
  async openAuditSession(token: string): Promise<LoginResponse & { reportId: number | null }> {
    const { data, error } = await client.POST('/api/audit/session', { body: { token } });
    if (error) throw new Error((error as any).message || 'That link is no longer valid');
    return {
      token: data!.tokens.accessToken,
      user: data!.user,
      reportId: data!.reportId ?? null,
    };
  },

  async getAuditReport(id: number) {
    const { data, error } = await client.GET('/api/audit/report/{id}', {
      params: { path: { id } },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Could not load that report');
    return data!.report;
  },

  async forgotPassword(email: string) {
    const { data, error } = await client.POST('/api/auth/forgot-password', { body: { email } });
    if (error) throw new Error((error as any).message || 'Request failed');
    return data!;
  },

  async resetPassword(token: string, password: string) {
    const { data, error } = await client.POST('/api/auth/reset-password', { body: { token, password } });
    if (error) throw new Error((error as any).message || 'Password reset failed');
    return data!;
  },

  // Inspect a file without importing it: returns the detected columns, a
  // proposed mapping, and a real sample value per field so the publisher can
  // see at a glance whether it is right (feed-column-mapping).
  async inspectFeed(file: File, network: string): Promise<FeedInspectResult> {
    const fd = new FormData();
    // Text fields before the file part — see the note in uploadFeed.
    fd.append('network', network);
    fd.append('file', file);

    const res = await appFetch(`${apiUrl}/api/affiliate-feeds/inspect`, {
      method: 'POST',
      headers: authHeaders() as any,
      body: fd,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.message || 'Could not read that file');
    return body as FeedInspectResult;
  },

  async uploadFeed(file: File, network: string, label?: string, columnMapping?: Record<string, string>): Promise<FeedUploadResult> {
    const { data, error } = await client.POST('/api/affiliate-feeds/upload', {
      body: { file: file as any, network: network as any, ...(label ? { label } : {}) },
      headers: authHeaders(),
      bodySerializer: (body: any) => {
        const fd = new FormData();
        // Text fields MUST precede the file part: the API reads
        // data.fields right after request.file() resolves, and multipart
        // fields trailing the file are only parsed once the file stream
        // has been consumed.
        fd.append('network', body.network);
        if (body.label) fd.append('label', body.label);
        if (columnMapping && Object.keys(columnMapping).length > 0) {
          fd.append('columnMapping', JSON.stringify(columnMapping));
        }
        fd.append('file', body.file);
        return fd;
      },
    });
    if (error) {
      // Refused/failed uploads (400) carry the structured result (issues,
      // aborted, row detail) — surface it instead of flattening to a message
      if ((error as any)?.result || (error as any)?.success === false) return error as any;
      throw new Error((error as any).message || 'Upload failed');
    }
    return data!;
  },

  async listFeeds() {
    const { data, error } = await client.GET('/api/affiliate-feeds/feeds', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch feeds');
    return data!;
  },

  async deleteFeed(id: number) {
    const { data, error } = await client.DELETE('/api/affiliate-feeds/feeds/{id}', {
      params: { path: { id } },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to delete feed');
    return data!;
  },

  async updateFeedEpc(id: number, epcEstimate: number | null, epcCurrency: string | null) {
    const { data, error } = await client.PATCH('/api/affiliate-feeds/feeds/{id}', {
      params: { path: { id } },
      body: { epcEstimate, epcCurrency },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to update feed EPC');
    return data!;
  },

  // Create a URL-backed feed (feed-auto-sync) — no upload needed, products
  // arrive on the first sync.
  // Fetch a sample of the URL and propose a mapping BEFORE connecting it — a
  // scheduled sync has nobody to ask, so the mapping is settled while the
  // publisher is still here (feed-column-mapping).
  async inspectFeedUrl(network: string, sourceUrl: string): Promise<FeedInspectResult> {
    const res = await appFetch(`${apiUrl}/api/affiliate-feeds/inspect-url`, {
      method: 'POST',
      headers: { ...(authHeaders() as any), 'Content-Type': 'application/json' },
      body: JSON.stringify({ network, sourceUrl }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.message || 'Could not read that feed');
    return body as FeedInspectResult;
  },

  async createFeed(network: string, sourceUrl: string, label?: string, columnMapping?: Record<string, string>) {
    const { data, error } = await client.POST('/api/affiliate-feeds/feeds', {
      body: {
        network: network as any,
        sourceUrl,
        ...(label ? { label } : {}),
        ...(columnMapping && Object.keys(columnMapping).length > 0 ? { columnMapping } : {}),
      } as any,
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to create feed');
    return data!;
  },

  // sourceUrl is write-only: pass it to SET a new URL (or null to clear it),
  // never to read the current one back — GET/PATCH responses only ever
  // carry sourceUrlDisplay (redacted).
  async updateFeedSync(id: number, patch: { sourceUrl?: string | null; syncEnabled?: boolean }) {
    const { data, error } = await client.PATCH('/api/affiliate-feeds/feeds/{id}', {
      params: { path: { id } },
      body: patch,
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to update feed');
    return data!;
  },

  // Tier 3 of advertiser-resolution: the manual fallback for rows the feed's
  // link and its own advertiser column both leave unresolved. null clears it.
  async updateFeedDeclaredAdvertiser(id: number, declaredAdvertiserHost: string | null) {
    const { data, error } = await client.PATCH('/api/affiliate-feeds/feeds/{id}', {
      params: { path: { id } },
      body: { declaredAdvertiserHost },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to update the declared advertiser');
    return data!;
  },

  // The portal "Suggest" button — reads this feed's own already-resolved
  // products, fires no links. Unanimous → suggestion; disagreeing → null with
  // every distinct value named.
  async suggestDeclaredAdvertiser(id: number) {
    const { data, error } = await client.GET('/api/affiliate-feeds/feeds/{id}/suggest-advertiser', {
      params: { path: { id } },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to suggest an advertiser');
    return data!;
  },

  // Manual "Sync now" — runs the same pipeline inline and returns the
  // upload-style result so the publisher gets immediate feedback.
  async syncFeedNow(id: number): Promise<FeedUploadResult> {
    const { data, error } = await client.POST('/api/affiliate-feeds/feeds/{id}/sync', {
      params: { path: { id } },
      headers: authHeaders(),
    });
    if (error) {
      if ((error as any)?.result || (error as any)?.success === false) return error as any;
      throw new Error((error as any).message || 'Sync failed');
    }
    return data!;
  },

  async getShops(): Promise<ShopOffer> {
    const { data, error } = await client.GET('/api/user/shops', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to load your shops');
    return data as ShopOffer;
  },

  // A SECOND read for the Feeds page, fetched in parallel with getShops and
  // allowed to fail: outcome is decision support, not part of rendering a row.
  async getShopOutcome(): Promise<ShopOutcome> {
    const { data, error } = await client.GET('/api/user/shops/outcome', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to load shop outcome');
    return data as ShopOutcome;
  },

  async subscribeToCatalogue(network: string, programmeKey: string): Promise<SubscribeResult> {
    const { data, error } = await client.POST('/api/user/catalogues/subscribe', {
      body: { network, programmeKey },
      headers: authHeaders(),
    });
    // The 409 carries the only message worth showing — "your network does not
    // show you as approved" — so it is surfaced rather than flattened into a
    // generic failure.
    if (error) throw new Error((error as any).message || 'Failed to add advertiser');
    return data as SubscribeResult;
  },

  async unsubscribeFromCatalogue(catalogueId: number): Promise<void> {
    const { error } = await client.DELETE('/api/user/catalogues/{id}', {
      params: { path: { id: catalogueId } },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to remove advertiser');
  },

  async getCatalogCoverage(): Promise<CatalogCoverage> {
    const { data, error } = await client.GET('/api/user/catalog-coverage', {
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to fetch catalog coverage');
    return data as CatalogCoverage;
  },

  async getAnalytics(period?: AnalyticsPeriod): Promise<UserAnalyticsResponse> {
    const { data, error } = await client.GET('/api/user/analytics', {
      params: { query: period ? { period } : undefined },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to fetch analytics');
    return data!;
  },

  async getUsage(): Promise<UserUsageResponse> {
    const { data, error } = await client.GET('/api/user/usage', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch usage');
    return data!;
  },

  async getNetworkConnections(): Promise<NetworkConnection[]> {
    const { data, error } = await client.GET('/api/user/conversions/connections', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to load networks');
    return data!.connections;
  },

  async getMeasurementReadiness(): Promise<NetworkReadiness[]> {
    const { data, error } = await client.GET('/api/user/conversions/readiness', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to load measurement status');
    return data!.networks;
  },

  async getMeasuredEarnings(): Promise<MeasuredEarningsResponse> {
    const { data, error } = await client.GET('/api/user/conversions/earnings', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to load measured earnings');
    return data!;
  },

  // The route verifies the credential against the network live before storing
  // it, then runs a first sweep — so this call can legitimately take seconds.
  // Failures carry the API's own message (refused vs unreachable vs
  // unconfigured are different advice) and are returned rather than thrown so
  // the form can render them in place.
  async connectNetworkCredential(
    connectionId: number,
    body: { credential: string; accountRef?: string; defaultCurrency?: string }
  ): Promise<ConnectCredentialResult> {
    const { data, error } = await client.POST('/api/user/conversions/connections/{id}/credential', {
      params: { path: { id: connectionId } },
      body,
      headers: authHeaders(),
    });
    if (error) {
      return { success: false, message: (error as any).message || 'Could not save that credential.' };
    }
    return data!;
  },

  async uploadConversionsExport(connectionId: number, file: File, periodEnd?: string): Promise<ConversionUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('connectionId', String(connectionId));
    if (periodEnd) formData.append('periodEnd', periodEnd);
    const { data, error } = await client.POST('/api/user/conversions/upload', {
      // openapi-fetch serializes objects to JSON by default; hand it the
      // FormData through bodySerializer so the browser sets the multipart
      // boundary itself.
      body: formData as any,
      bodySerializer: (b: any) => b,
      headers: authHeaders(),
    });
    if (error) {
      return { success: false, message: (error as any).message || 'Could not process that file.' };
    }
    return data!;
  },

  async dismissMeasurementOffer(connectionId: number): Promise<void> {
    const { error } = await client.POST('/api/user/conversions/connections/{id}/dismiss', {
      params: { path: { id: connectionId } },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Could not save that.');
  },

  async getSetupStatus(): Promise<SetupStatusResponse> {
    const { data, error } = await client.GET('/api/user/setup-status', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch setup status');
    return data!;
  },

  // Mint a short-lived, domain-bound debug token and get the launch URL for
  // the on-site debug sidebar. The server binds the domain to the account's
  // registered one — there is nothing to pass.
  async mintDebugToken(): Promise<DebugTokenResponse> {
    const { data, error } = await client.POST('/api/user/debug-token', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to start live-site debugging');
    return data!;
  },


  async getApiKey(): Promise<string> {
    const { data, error } = await client.GET('/api/user/api-key', { headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch API key');
    return data!.apiKey;
  },

  async getFeedHistory(params?: { limit?: number; offset?: number; network?: string; feedRef?: number }) {
    const { data, error } = await client.GET('/api/affiliate-feeds/history', { params: { query: params }, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch feed history');
    return data!;
  },

  async getFeedStats(params?: { network?: string; days?: number }) {
    const { data, error } = await client.GET('/api/affiliate-feeds/stats', { params: { query: params }, headers: authHeaders() });
    if (error) throw new Error((error as any).message || 'Failed to fetch feed stats');
    return data!;
  },

  async deleteNetworkProducts(network: string) {
    const { data, error } = await client.DELETE('/api/affiliate-feeds/products/{network}', {
      params: { path: { network } },
      headers: authHeaders(),
    });
    if (error) throw new Error((error as any).message || 'Failed to delete products');
    return data!;
  },
};
